import { createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

export const COMPARE_LISTINGS_REQUEST = 'app/ComparePage/COMPARE_LISTINGS_REQUEST';
export const COMPARE_LISTINGS_SUCCESS = 'app/ComparePage/COMPARE_LISTINGS_SUCCESS';
export const COMPARE_LISTINGS_ERROR = 'app/ComparePage/COMPARE_LISTINGS_ERROR';

const initialState = {
  listingIds: [],
  compareListingsInProgress: false,
  compareListingsError: null,
};

const comparePageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case COMPARE_LISTINGS_REQUEST:
      return {
        ...state,
        compareListingsInProgress: true,
        compareListingsError: null,
      };
    case COMPARE_LISTINGS_SUCCESS:
      return {
        ...state,
        listingIds: payload.listingIds,
        compareListingsInProgress: false,
      };
    case COMPARE_LISTINGS_ERROR:
      return {
        ...state,
        compareListingsInProgress: false,
        compareListingsError: payload,
      };
    default:
      return state;
  }
};

export default comparePageReducer;

export const compareListingsRequest = () => ({
  type: COMPARE_LISTINGS_REQUEST,
});

export const compareListingsSuccess = listingIds => ({
  type: COMPARE_LISTINGS_SUCCESS,
  payload: { listingIds },
});

export const compareListingsError = e => ({
  type: COMPARE_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const RESULT_PAGE_SIZE = 100;

// `deviceType` is a free-text public data field: listings store the human label
// (e.g. "Caméra de sécurité"), while URLs/ids use the slugified form
// (e.g. "camera-de-securite"). The server filter matches the raw stored string,
// so we must query with the label, not the slug. `id` is createSlug(pubValue).
export const SUPPORTED_DEVICE_TYPES = [
  { id: 'camera-de-securite', pubValue: 'Caméra de sécurité' },
  { id: 'thermostat', pubValue: 'Thermostat' },
  { id: 'prise-connectee', pubValue: 'Prise connectée' },
  { id: 'serrure-connectee', pubValue: 'Serrure connectée' },
];

export const SUPPORTED_DEVICE_TYPE_IDS = SUPPORTED_DEVICE_TYPES.map(d => d.id);

const DEVICE_TYPE_ID_TO_PUB_VALUE = new Map(
  SUPPORTED_DEVICE_TYPES.map(d => [d.id, d.pubValue])
);

export const fetchCompareListings = (deviceTypeParam, config) => (dispatch, getState, sdk) => {
  dispatch(compareListingsRequest());

  if (!sdk || !sdk.listings || typeof sdk.listings.query !== 'function') {
    dispatch(compareListingsError(new Error('SDK not available for fetching listings')));
    return Promise.resolve();
  }

  const deviceType = (deviceTypeParam || '').toLowerCase().trim();
  const pubDeviceType = deviceType
    ? DEVICE_TYPE_ID_TO_PUB_VALUE.get(deviceType) || deviceTypeParam
    : `has_any:${SUPPORTED_DEVICE_TYPES.map(d => d.pubValue).join(',')}`;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const baseParams = {
    pub_deviceType: pubDeviceType,
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'description',
      'price',
      'deleted',
      'state',
      'metadata',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.cardStyle',
      'publicData.brand',
      'publicData.original_price',
      'publicData.sales',
      'publicData.deviceType',
      'publicData.connectivity',
      'publicData.shortdescription',
      'publicData.ref_manufacturer',
      'publicData.device_specific_specs',
      'publicData.priceVariationsEnabled',
      'publicData.priceVariants',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
    perPage: RESULT_PAGE_SIZE,
  };

  const allListings = [];
  const allIncluded = [];

  const fetchPage = page =>
    sdk.listings
      .query({ ...baseParams, page })
      .then(response => {
        const data = response?.data || {};
        const listings = data?.data || [];
        const included = data?.included || [];

        if (Array.isArray(listings) && listings.length > 0) {
          allListings.push(...listings);
        }
        if (Array.isArray(included) && included.length > 0) {
          allIncluded.push(...included);
        }

        const meta = data?.meta || {};
        return meta?.totalPages || 1;
      });

  return fetchPage(1)
    .then(totalPages => {
      let sequence = Promise.resolve();
      for (let page = 2; page <= totalPages; page += 1) {
        sequence = sequence.then(() => fetchPage(page));
      }
      return sequence;
    })
    .then(() => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      const response = {
        data: {
          data: allListings,
          meta: {
            totalItems: allListings.length,
            totalPages: 1,
            page: 1,
            perPage: allListings.length || RESULT_PAGE_SIZE,
          },
          included: allIncluded,
        },
      };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(compareListingsSuccess(allListings.map(l => l.id)));
      return response;
    })
    .catch(e => {
      dispatch(compareListingsError(storableError(e)));
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const deviceType = params?.device_type;
  return dispatch(fetchCompareListings(deviceType, config));
};
